/* eslint-disable react/no-multi-comp */
import { useEffect } from 'react';
import dynamic from 'next/dynamic';
// Components
import Link from 'next/link';
import { useRouter } from 'next/router';

import { TranslationService } from '@/utils/services/TranslationService';

import useAcl from '@/hooks/user/useAcl';
// Hooks
import useUser from '@/utils/hooks/swr/useUser';
import useTranslation from '@/utils/hooks/translation/useTranslation';
import useUserRole from '@/utils/hooks/user/useUserRole';

// Utils
import useMarketingUrl from '@/utils/urls/useMarketingUrl';
import useToggle from '@/hooks/utils/useToggle';
import useRouterQuery from '@/utils/hooks/utils/useRouterQuery';

// Lib
import { aclFeatures } from '@/lib/acl/aclFeatures';
import { REMOVE_YOUR_ACCOUNT } from '@/lib/settings/account/steps';

import Button from '@/components/base/Button';
import H3 from '@/components/base/Headings/H3';
import Tooltip from '@/components/base/Tooltip';
import { WrapperSwitchLayout } from '@/components/layouts/wrappers/WrapperSwitchLayout';
import AccountForm from '@/components/pages/settings/AccountForm';
import { RecapCurrentPlan } from '@/components/pages/settings/subscription-invoices/CurrentUserPlan/RecapCurrentPlan';
import SettingsWrapper from '@/components/layouts/LayoutPlatform/components/SettingsWrapper';

// Endpoints
import { ACCOUNT, SECURITY } from '@/routes/privateRoutes';
const ModalDeleteAccount = dynamic(
    () => import('@/components/composite/Modals/ModalDeleteAccount')
);

const PageContent = () => {
    return (
        <SettingsWrapper>
            <SettingsWrapper.MainContent>
                <AccountForm />
            </SettingsWrapper.MainContent>
            <SettingsWrapper.SideContent>
                <SideContent />
            </SettingsWrapper.SideContent>
        </SettingsWrapper>
    );
};

const SideContent = () => {
    const { marketingUrl } = useMarketingUrl();
    const { t } = useTranslation('settings');
    const { hasAcl } = useAcl();
    const { isAdmin } = useUserRole();
    const { user } = useUser();
    const router = useRouter();
    const { step } = router.query;
    const { isOpen: isOpenModalDeleteAccount, toggle: toggleModalDeleteAccount } = useToggle({
        initial: step === REMOVE_YOUR_ACCOUNT && hasAcl(aclFeatures.account.base.delete)
    });

    const { deleteQuery, addQueryParams } = useRouterQuery();

    useEffect(() => {
        if (!isOpenModalDeleteAccount && step === REMOVE_YOUR_ACCOUNT) {
            router.replace(deleteQuery('step'));
        }
        if (isOpenModalDeleteAccount && step !== REMOVE_YOUR_ACCOUNT) {
            addQueryParams([`step=${REMOVE_YOUR_ACCOUNT}`]);
        }
    }, [isOpenModalDeleteAccount]);

    useEffect(() => {
        if (
            step === REMOVE_YOUR_ACCOUNT &&
            !isOpenModalDeleteAccount &&
            hasAcl(aclFeatures.account.base.delete) &&
            isAdmin
        ) {
            toggleModalDeleteAccount();
        } else if (step) {
            router.replace(deleteQuery('step'));
        }
    }, [step]);

    return (
        <>
            <div className="border-b border-icoGray-300 p-8 dark:border-icoDarkMode-beton xl:px-7 xl:py-10">
                <RecapCurrentPlan />
            </div>
            <div className="border-b border-icoGray-300 p-8 dark:border-icoDarkMode-beton xl:px-7 xl:py-10 xl:pb-10">
                <H3 className="mb-m">{t('account_page.additional_column.change_password')}</H3>
                <p className="mb-8 font-GSemiBold text-xs leading-4 text-icoGray-500 dark:text-icoDarkMode-wolf">
                    {t('account_page.additional_column.change_password_notice')}
                </p>
                <Button as={Link} href={SECURITY} className="w-full" size="md" variant="secondary">
                    {t('account_page.additional_column.change_password')}
                </Button>
            </div>
            <div className="border-b border-icoGray-300 p-8 dark:border-icoDarkMode-beton xl:px-7 xl:py-10 xl:pb-10">
                <H3 className="mb-m">{t('account_page.additional_column.remove_your_account')}</H3>
                <p className="mb-8 font-GSemiBold text-xs leading-4 text-icoGray-500 dark:text-icoDarkMode-wolf">
                    {t('account_page.additional_column.remove_your_account_notice')}
                </p>
                {isAdmin ? (
                    <Button
                        as={Link}
                        href={
                            hasAcl(aclFeatures.account.base.delete)
                                ? {
                                      pathname: ACCOUNT,
                                      query: {
                                          step: REMOVE_YOUR_ACCOUNT
                                      }
                                  }
                                : marketingUrl('contact')
                        }
                        target={hasAcl(aclFeatures.account.base.delete) ? '_self' : '_blank'}
                        size="md"
                        variant="secondary"
                        className="w-full"
                        rel={
                            hasAcl(aclFeatures.account.base.delete) ? null : 'noopener noreferrer'
                        }>
                        {t('account_page.additional_column.remove_your_account')}
                    </Button>
                ) : (
                    <Tooltip
                        content={t(
                            user?.admin_email
                                ? 'account_page.additional_column.tooltip_delete_account_team_member_email'
                                : 'account_page.additional_column.tooltip_delete_account_team_member',
                            { email: user?.admin_email ?? '' }
                        )}
                        placement="top">
                        <span>
                            <Button size="md" variant="secondary" className="w-full" disabled>
                                {t('account_page.additional_column.remove_your_account')}
                            </Button>
                        </span>
                    </Tooltip>
                )}
            </div>
            <ModalDeleteAccount
                isOpen={isOpenModalDeleteAccount}
                toggle={toggleModalDeleteAccount}
            />
        </>
    );
};

export const getServerSideProps = TranslationService.getTranslationMessagesFromRoute(ACCOUNT);

export default function Account() {
    return (
        <WrapperSwitchLayout noPadding={true} disableMainScroll={true}>
            <PageContent />
        </WrapperSwitchLayout>
    );
}
